import { useState } from "react";
import { clsx } from "clsx";
import {
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Activity,
  LogOut,
  LayoutDashboard,
  HelpCircle,
  Building2,
  Receipt,
  Calendar,
  Users,
  TrendingUp,
  FileText,
  Settings,
  Image,
  Sparkles,
  Dumbbell
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { logoutUser } from "../../../redux/auth/authAction";
import styles from "./Sidebar.module.scss";

// Icon mapping helper
const getIcon = (iconName) => {
  if (!iconName) return HelpCircle;

  const lowerName = iconName.toLowerCase();



  // If it's a FontAwesome class
  if (iconName.includes("fa-") || iconName.startsWith("fa")) {
    return iconName;
  }

  // Support both PascalCase and camelCase icon names from backend
  const Icon =
    LucideIcons[iconName] ||
    LucideIcons[iconName.charAt(0).toUpperCase() + iconName.slice(1)];
  return Icon || HelpCircle;
};

const normalizeUrl = (url, variant, label = "") => {
  const cleanLabel = (label || "").toLowerCase();

  if (
    cleanLabel.includes("user list") ||
    cleanLabel.includes("userlist") ||
    cleanLabel.includes("users") ||
    cleanLabel.includes("client list") ||
    cleanLabel.includes("clients")
  ) {
    return variant === "admin" ? "/admin/users" : "/app/clients";
  }

  if (
    !url ||
    url === "/" ||
    url === "" ||
    url.toLowerCase().includes("dashboard") ||
    cleanLabel.includes("dashboard")
  ) {
    return variant === "admin" ? "/admin/dashboard" : "/app/dashboard";
  }

  // Strip protocol and host if present
  let cleanUrl = url.toLowerCase();
  try {
    if (cleanUrl.startsWith("http")) {
      const urlObj = new URL(cleanUrl);
      cleanUrl = urlObj.pathname;
    }
  } catch {
    cleanUrl = url.toLowerCase();
  }

  // Clean trailing slashes and normalize
  let lowerUrl = cleanUrl.replace(/\/$/, "");
  if (!lowerUrl.startsWith("/")) lowerUrl = "/" + lowerUrl;

  const mapping = {
    "/trainer": "/app/trainers",
    "/trainer/index": "/app/trainers",
    "/trainers": "/app/trainers",
    "/role/index": "/app/roles",
    "/userlist/index": "/app/users",
    "/subscription/index": "/app/subscription",
    "/subscription": "/app/subscription",
    "/subscriptions": "/app/subscription",
    "/diet": "/app/nutrition",
    "/diet/index": "/app/nutrition",
    "/nutrition": "/app/nutrition",
    "/workout/exercises": "/app/exercises",
    "/exercises": "/app/exercises",
    "/exercise": "/app/exercises",
    "/exerciselibrary/index": "/app/exercises",
    "/workout/workoutplans": "/app/workouts",
    "/workout/index": "/app/workouts",
    "/workouts": "/app/workouts",
    "/workout": "/app/workouts",
    "/schedule": "/app/schedule",
    "/scheduling": "/app/schedule",
    "/attendance": "/app/attendance",
    "/attendance/index": "/app/attendance",

    "/clients": "/app/clients",
    "/client": "/app/clients",
    "/orders": "/app/orders",
    "/payments": "/app/payments",
    "/revenue": "/app/revenue",
    "/reports": "/app/reports",
    "/settings": "/app/settings",
    "/profile": "/app/profile",
    "/leads": "/app/leads",
    "/crm/leads": "/app/leads",
    "/inventory": "/app/inventory",
    "/pos": "/app/pos",
    "/executive": "/app/executive",
    "/businessexecutive": "/app/executive",
    "/marketing": "/app/post",
    "/marketing/index": "/app/post",
    "/post": "/app/post",
  };

  if (mapping[lowerUrl]) return mapping[lowerUrl];


  // Fuzzy match for any lead/crm variation
  if (lowerUrl.includes("lead") || lowerUrl.includes("crm")) {
    return "/app/leads";
  }

  if (lowerUrl.includes("inventory")) {
    return "/app/inventory";
  }

  if (
    lowerUrl === "/pos" ||
    lowerUrl === "/app/pos" ||
    lowerUrl.endsWith("/pos")
  ) {
    return "/app/pos";
  }

  // If it's a client variant and doesn't start with /app or /admin, prefix with /app
  if (
    variant === "client" &&
    !lowerUrl.startsWith("/app") &&
    !lowerUrl.startsWith("/admin")
  ) {
    return `/app${lowerUrl}`;
  }

  return lowerUrl;
};

const getDynamicMenuItems = (apiMenus, variant) => {
  if (!apiMenus || apiMenus.length === 0) return [];

  const flattenedItems = [];
  apiMenus.forEach((menu) => {
    // Add Parent Menu as Header if it has submenus, or as Item if it doesn't
    if (menu.subMenus && menu.subMenus.length > 0) {
      flattenedItems.push({
        type: "header",
        label: menu.menuName.toUpperCase(),
      });
      menu.subMenus.forEach((sub) => {
        flattenedItems.push({
          id: sub.menuId.toString(),
          label: sub.menuName,
          icon: getIcon(sub.icon),
          path: normalizeUrl(sub.url, variant, sub.menuName),
          type: "item",
        });
      });
    } else {
      flattenedItems.push({
        id: menu.menuId.toString(),
        label: menu.menuName,
        icon: getIcon(menu.icon),
        path: normalizeUrl(menu.url, variant, menu.menuName),
        type: "item",
      });
    }
  });

  return flattenedItems;
};

const ensureMenuItem = (menuItems, item) => {
  const exists = menuItems.some(
    (menuItem) => menuItem.path === item.path || menuItem.id === item.id,
  );

  if (!exists) {
    menuItems.push(item);
  }
};

const Sidebar = ({ variant = "client", activeItem, onNavigate, onToggle }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isPromoExpanded, setIsPromoExpanded] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { menus: apiMenus } = useSelector((state) => state.menu);

  const menuItems = getDynamicMenuItems(apiMenus, variant);

  // Ensure Dashboard is always at the top of the sidebar menu
  const dashboardPath = variant === "admin" ? "/admin/dashboard" : "/app/dashboard";
  const hasDashboard = menuItems.some(
    (item) => item.path === dashboardPath && item.label.toLowerCase().includes("dashboard")
  );
  if (!hasDashboard) {
    menuItems.unshift({
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      path: dashboardPath,
      type: "item",
    });
  }

  if (variant === "client") {
    [
      {
        id: "leads",
        label: "Leads",
        icon: Users,
        path: "/app/leads",
        type: "item",
      },
      {
        id: "inventory",
        label: "Inventory",
        icon: Building2,
        path: "/app/inventory",
        type: "item",
      },
      {
        id: "pos",
        label: "POS",
        icon: Receipt,
        path: "/app/pos",
        type: "item",
      },
      {
        id: "marketing-studio",
        label: "Marketing Studio",
        icon: Image,
        path: "/app/post",
        type: "item",
      },
      {
        id: "user-feedback",
        label: "User Feedback",
        icon: FileText,
        path: "/app/user-feedback-list",
        type: "item",
      },
      {
        id: "executive-hub",
        label: "Executive Hub",
        icon: TrendingUp,
        path: "/app/executive",
        type: "item",
      },
      {
        id: "settings",
        label: "Settings",
        icon: Settings,
        path: "/app/settings",
        type: "item",
      },
    ].forEach((item) => ensureMenuItem(menuItems, item));
  }

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onToggle?.(newState);
  };

  const handleNavigate = (path) => {
    onNavigate?.(path);
    setIsMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser());
    } finally {
      navigate("/");
    }
  };

  return (
    <>
      {/* Mobile Toggle */}
      <button
        className={styles.mobileToggle}
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay */}
      {isMobileOpen && (
        <div
          className={styles.overlay}
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          styles.sidebar,
          styles[variant],
          isCollapsed && styles.collapsed,
          isMobileOpen && styles.mobileOpen,
        )}
      >
        {/* Logo */}
        <div className={styles.logo}>
          {isCollapsed ? (
            <img
              src={variant === "admin" ? "/assets/images/logo-collapsed-light.svg" : "/assets/images/logo-collapsed.svg"}
              alt="BizAura Icon"
              className={styles.logoIconImg}
            />
          ) : (
            <img
              src={variant === "admin" ? "/assets/images/logo-text-light.svg" : "/assets/images/logo-text.svg"}
              alt="BizAura Logo"
              className={styles.logoImg}
            />
          )}
        </div>

        {/* Collapse Toggle (Desktop) */}
        <button className={styles.collapseToggle} onClick={handleToggle}>
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>

        {/* Main Menu */}
        <nav className={styles.nav}>
          <ul className={styles.menu}>
            {menuItems.map((item, index) => {
              if (item.type === "header") {
                return (
                  !isCollapsed && (
                    <li key={`header-${index}`} className={styles.menuHeader}>
                      <span>{item.label}</span>
                    </li>
                  )
                );
              }

              const iconContent =
                typeof item.icon === "string" ? (
                  <i
                    className={clsx(item.icon, styles.faIcon)}
                    style={{
                      fontSize: "18px",
                      width: "20px",
                      textAlign: "center",
                    }}
                  ></i>
                ) : (
                  <item.icon size={20} />
                );

              const isExact = location.pathname === item.path;
              const isDashboard =
                item.path === "/app/dashboard" &&
                location.pathname === "/app/dashboard";
              const isChild =
                item.path !== "/app/dashboard" &&
                item.path &&
                location.pathname.startsWith(item.path + "/");
              const isActive =
                activeItem === item.id || isExact || isDashboard || isChild;

              return (
                <li key={item.id}>
                  <button
                    className={clsx(styles.menuItem, isActive && styles.active)}
                    onClick={() => handleNavigate(item.path)}
                    title={isCollapsed ? item.label : undefined}
                  >
                    {iconContent}
                    <span className={styles.menuLabel}>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Premium Membership Sidebar Promo Widget */}
        {variant === "client" && (
          <div className={clsx(styles.promoCardContainer, isCollapsed && styles.promoCollapsed)}>
            {!isCollapsed ? (
              isPromoExpanded ? (
                <div className={styles.promoCard}>
                  <button
                    type="button"
                    className={styles.promoCloseBtn}
                    onClick={() => setIsPromoExpanded(false)}
                    title="Collapse offer"
                  >
                    <ChevronDown size={15} />
                  </button>

                  <div className={styles.promoImageWrapper}>
                    <img
                      src="/assets/images/premium-upgrade.png"
                      alt="Premium Membership"
                      className={styles.promoImage}
                    />
                  </div>

                  <h4 className={styles.promoTitle}>Premium Membership</h4>
                  <p className={styles.promoSubtitle}>
                    Monitor progress, set goals, and achieve results faster!
                  </p>
                  <button
                    type="button"
                    className={styles.promoUpgradeBtn}
                    onClick={() => handleNavigate("/app/subscription")}
                  >
                    Upgrade
                  </button>
                </div>
              ) : (
                <div
                  className={styles.promoMiniBar}
                  onClick={() => setIsPromoExpanded(true)}
                  title="Expand Premium Membership Offer"
                >
                  <div className={styles.miniBarLeft}>
                    <Sparkles size={16} color="#0d9488" />
                    <span>Premium Membership</span>
                  </div>
                  <ChevronUp size={16} className={styles.miniBarArrow} />
                </div>
              )
            ) : (
              <button
                type="button"
                className={styles.promoIconOnlyBtn}
                onClick={() => handleNavigate("/app/subscription")}
                title="Upgrade to Premium Membership"
              >
                <Sparkles size={20} color="#0d9488" />
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
